import {
    Badge,
    Box,
    Button,
    Heading,
    HStack,
    SimpleGrid,
    Text
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import {
    MdBusiness,
    MdEmail,
    MdFingerprint,
    MdLocalShipping,
    MdLocationOn, MdPerson, MdPhone,
    MdScience
} from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { fetchFacilityPickupsSummary, getFacilityById } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import { useTableColors } from '../../Utils';
import FacilityAddNotes from './FacilityAddNotes';

export default function FacilityDetailsPage() {
    const { id } = useParams();
    const [facility, setFacility] = useState(null);
    const [error, setError] = useState('');
    const [mapReady, setMapReady] = useState(false);
    const mapRef = useRef(null);
    const [mapExpanded, setMapExpanded] = useState(false);
    const [pickupsSummary, setPickupsSummary] = useState(null);
    const coolors = useTableColors();
    useEffect(() => {
        if (id) fetchData();
    }, [id]);

    const fetchData = async () => {
        const res = await getFacilityById(id);
        if (!res.success) return setError(res.error);
        setFacility(res.data);

        // Fetch pickups summary
        const summaryRes = await fetchFacilityPickupsSummary(id);
        setPickupsSummary(summaryRes.success ? summaryRes.data : null);


        if (!window.mapkit || !res.data?.address || mapReady) return;

        const { address1, address2, city, state, zip } = res.data.address;
        const full = `${address1} ${address2 || ''} ${city} ${state} ${zip}`;

        // Delay to ensure DOM is ready
        setTimeout(() => {
            const mapContainer = document.getElementById('facility-map');
            if (!mapContainer) return;

            window.mapkit.init({
                authorizationCallback: done => done(process.env.REACT_APP_MAP_TOKEN),
            });

            const map = new window.mapkit.Map(mapContainer, {
                mapType: window.mapkit.Map.MapTypes.Standard,
                isZoomEnabled: true,
                isScrollEnabled: true,
                showScare: true,
                showsCompass: true
            });

            const geocoder = new window.mapkit.Geocoder();
            geocoder.lookup(full, (err, data) => {
                if (!err && data?.results?.length) {
                    const { coordinate } = data.results[0];
                    const region = new window.mapkit.CoordinateRegion(
                        coordinate,
                        new window.mapkit.CoordinateSpan(0.02, 0.02)
                    );
                    map.region = region;

                    const annotation = new window.mapkit.MarkerAnnotation(coordinate, {
                        title: res.data.name,
                        subtitle: full,
                        color: "#007aff",
                    });

                    map.addAnnotation(annotation);
                    mapRef.current = map;
                    setMapReady(true);
                }
            });
        }, 0);
    };

    if (!facility) return <Text p={8}>Loading...</Text>;
    const address = facility.address;

    return (
        <Box>
            {error ? (
                <AccessDenied msg={error} />
            ) : (
                <>
                    {/* Header */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} mt={6} spacing={4} mb={6}>
                        <Box>
                            <Heading size="lg">{facility.name}</Heading>


                            <HStack spacing={3} mt={2} flexWrap="wrap">
                                <Badge colorScheme={facility.hasLockbox ? 'green' : 'red'}>
                                    Lockbox: {facility.hasLockbox ? 'Yes' : 'No'}
                                </Badge>
                                {facility.createdBy?.firstName && (
                                    <Badge colorScheme="purple">Added by: {facility.createdBy?.firstName}</Badge>
                                )}
                                {facility.salesRep?.name && (
                                    <Badge colorScheme="blue">Sales Rep: {facility.salesRep.name}</Badge>
                                )}
                            </HStack>

                        </Box>
                    </SimpleGrid>

                    {/* Apple Map Banner */}
                    <Box w="100%" h={mapExpanded ? '700px' : '300px'} position="relative">
                        <Box id="facility-map" w="100%" h="100%" />

                        <Button
                            size="xs"
                            position="absolute"
                            bottom="10px"
                            right="150px"
                            zIndex="10"
                            onClick={() => setMapExpanded(!mapExpanded)}
                        >
                            {mapExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                    </Box>
                    {/* Overview */}
                    <Box borderWidth="1px" bg={coolors.theadBg} p={4} mb={6}>
                        <Heading size="md" mb={2}>
                            <HStack><MdBusiness /><Text>Overview</Text></HStack>
                        </Heading>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <HStack spacing={3}>
                                <MdFingerprint />
                                <Box>
                                    <Text fontWeight="semibold">Client Number</Text>
                                    <Text>{facility.clientNumber}</Text>
                                </Box>
                            </HStack>

                            <HStack spacing={3}>
                                <MdScience />
                                <Box>
                                    <Text fontWeight="semibold">Laboratory</Text>
                                    <Text>{facility.lab?.name}</Text>
                                </Box>
                            </HStack>
                        </SimpleGrid>
                    </Box>


                    {/* Address + Contact */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                        {/* Address */}
                        <Box bg={coolors.theadBg} borderWidth="1px" rounded="md" p={4}>
                            <Heading size="md" mb={2}>
                                <HStack><MdLocationOn /><Text>Address</Text></HStack>
                            </Heading>
                            <Text>
                                {`${address.address1}${address.address2 ? ', ' + address.address2 : ''}, ${address.city}, ${address.state} ${address.zip}`}
                            </Text>
                        </Box>

                        {/* Contact */}
                        <Box bg={coolors.theadBg} borderWidth="1px" rounded="md" p={4}>
                            <Heading size="md" mb={2}>
                                <HStack><MdPerson /><Text>Contact</Text></HStack>
                            </Heading>

                            <HStack spacing={2} mt={2}>
                                <Text fontWeight="semibold">Person:</Text>
                                <Text>{facility.contact?.person || 'N/A'}</Text>
                            </HStack>

                            <HStack spacing={2} mt={2}>
                                <MdPhone />
                                <Text>{facility.contact?.phone || 'N/A'}</Text>
                            </HStack>

                            <HStack spacing={2} mt={2}>
                                <MdEmail />
                                <Text>{facility.contact?.email || 'N/A'}</Text>
                            </HStack>

                            <HStack spacing={2} mt={2}>
                                <Text fontWeight="semibold">Sales Rep:</Text>
                                <Text>{facility.salesRep?.name || 'N/A'}</Text>
                            </HStack>
                        </Box>
                    </SimpleGrid>

                    {pickupsSummary && (
                        <Box borderWidth="1px" rounded="md" p={4} bg={coolors.theadBg} mb={6}>
                            <Heading size="md" mb={2}>
                                <HStack>
                                    <MdLocalShipping />
                                    <Text>Pickups & Supplies Summary</Text>
                                </HStack>
                            </Heading>
                            <Text mb={6} fontSize="sm" color="grey">
                                These numbers are based on pickups and supply orders received by the Laboratory.
                            </Text>
                            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                                {/* Pickups */}
                                <Box>
                                    <Text fontWeight="semibold">Total Pickups</Text>
                                    <Text>{pickupsSummary.totalPickups}</Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold">Last Pickup</Text>
                                    <Text>
                                        {pickupsSummary.lastPickupDate
                                            ? new Date(pickupsSummary.lastPickupDate).toLocaleString()
                                            : 'N/A'}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold">Total Samples</Text>
                                    <Text>{pickupsSummary.totalSamples}</Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold">Avg Samples / Pickup</Text>
                                    <Text>{Number(pickupsSummary.avgSamplesPerPickup).toFixed(2)}</Text>
                                </Box>

                                {/* Supplies Orders */}
                                <Box>
                                    <Text fontWeight="semibold">Total Supplies Orders</Text>
                                    <Text>{pickupsSummary.totalOrders}</Text>
                                </Box>
                                <Box>
                                    <Text fontWeight="semibold">Last Order Date</Text>
                                    <Text>
                                        {pickupsSummary.lastOrderDate
                                            ? new Date(pickupsSummary.lastOrderDate).toLocaleString()
                                            : 'N/A'}
                                    </Text>
                                </Box>

                                {/* Combined */}
                                <Box>
                                    <Text fontWeight="semibold">Avg Supplies Orders / Pickup</Text>
                                    <Text>{pickupsSummary.avgSuppliesRequestsPerPickup}</Text>
                                </Box>
                            </SimpleGrid>
                        </Box>
                    )}

                    {/* Notes */}
                    <Box borderWidth="1px" rounded="md" bg={coolors.theadBg} p={4} mb={6}>
                        <Heading size="md" mb={9}>Notes</Heading>
                        <FacilityAddNotes
                            facility={facility}
                        />
                    </Box>

                </>
            )}
        </Box>
    );
}
